import React, { FunctionComponent, useEffect, useState } from 'react';
import { ComponentRendering, isEditorActive, Placeholder, RichText } from '@sitecore-jss/sitecore-jss-react';
import breakpoints from '../../../constants/breakpoints.constants';
import ctaTypes from '../../../enums/CTA.enums';
import useMediaQuery from '../../../hooks/useMediaQuery';
import getNetworkDownloadSpeed from '../../../utils/getNetworkDownloadSpeed.utils';
import { targetLinkdsk } from '../../../utils/targetDskLink';
import CTA from '../../@shared/CTA';
import { CustomText } from '../../@shared/customText';
import DownloadCTA from '../../@shared/downloadCTA';
import FadeDown from '../../@shared/fadeDownEffect';
import { THeroCarouselItemProps } from '../types/herocarousel.types';
import MediaImage from './mediaImage';
import VideoPlayer from '../../@shared/videoPlayer';

const HeroCarouselSlide: FunctionComponent<THeroCarouselItemProps> = ({
  slideInfo,
  fields,
  rendering,
  isEEActive,
  id,
  slideTitleTag = 'h2',
  sitecoreContext,
}) => {
  const [mediaVisible, setMediaVisible] = useState({ video: false, image: false });
  const [networkSpeed, setNetworkSpeed] = useState<number | null>(null);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const textColor = fields?.textColor?.value;
  const isDesktop = useMediaQuery(`(min-width: ${breakpoints['tbl-p']}px)`);

  // eslint-disable-next-line
  useEffect(() => {
    const checkSpeed = async () => {
      try {
        const speed = await getNetworkDownloadSpeed();
        const parsedSpeed = parseInt(speed);
        setNetworkSpeed(parsedSpeed);

        if (!isFirstLoad && fields?.useVideo?.value) {
          const shouldShowVideo = isEditorActive() || parsedSpeed >= 15;
          setMediaVisible({
            video: shouldShowVideo,
            image: !shouldShowVideo,
          });
        }

        setIsFirstLoad(false);
      } catch (error) {
        setMediaVisible({ video: false, image: true });
      }
    };

    checkSpeed();
  }, [fields?.useVideo?.value, isFirstLoad]);

  useEffect(() => {
    if (slideInfo?.currentSlide === slideInfo?.index) {
      if (fields?.useVideo?.value && networkSpeed !== null) {
        const shouldShowVideo = networkSpeed >= 15 || isEditorActive();
        setMediaVisible({
          video: shouldShowVideo,
          image: !shouldShowVideo,
        });
      } else if (networkSpeed !== null) {
        setMediaVisible({
          video: false,
          image: true,
        });
      }
    }
  }, [slideInfo?.currentSlide, slideInfo?.index, networkSpeed, fields?.useVideo?.value]);

  const mediaElement = fields.useVideo?.value && (isEditorActive() || !networkSpeed || networkSpeed >= 15)
    ? mediaVisible.video && (
          <VideoPlayer
            link={fields?.video?.value?.href?.replace('sitecore/media-library', '-/media')}
            autoPlay={true}
            muted={true}
            playsInline={true}
            loop={true}
            controls={false}
            extraClassName={'slide__video'}
          />
        )
      : mediaVisible.image && (
          <MediaImage
            id={id}
            dataSource={rendering?.dataSource}
            desktopSlideImage={fields?.image}
            tabletSlideImage={fields?.mobileImage}
            mobileSlideImage={fields?.mobileImage}
            altText={`${fields?.image?.value?.alt}`}
            prioritizeSlide={slideInfo?.index}
            sitecoreContext={sitecoreContext}
          />
        );

  return (
    <div className={'slide__inner'} data-text-color={textColor}>
      {networkSpeed !== 0 ? mediaElement : null}
      <div className={'slide__content'}>
        {isEEActive ? (
          <>
            <CustomText field={fields?.title} tag={slideTitleTag} className={'slide__text slide__text--title'} />
            <RichText field={fields?.content} tag={'div'} className={'rich-text color-inherit-link slide__text slide__text--content'} />
            <div className="slide__buttons">
              <CTA type={ctaTypes.brandPrimary} field={fields.link} />
              <DownloadCTA type={ctaTypes['primaryWhite']} field={fields?.downloadButtonLink} />
              <div className="slide__buttons--popup-link generic-popupEE-width">
                <Placeholder name={'jss-generic-popup'} rendering={rendering as ComponentRendering} />
              </div>
            </div>
          </>
        ) : (
          <>
            <FadeDown duration={1000} delay={1000}>
              <CustomText field={fields?.title} tag={slideTitleTag} className={'slide__text slide__text--title'} />
            </FadeDown>
            <FadeDown duration={1000} delay={2000}>
              <RichText field={fields?.content} tag={'div'} className={'rich-text color-inherit-link slide__text slide__text--content'} />
            </FadeDown>
            <FadeDown duration={1000} delay={3000}>
              <div className="slide__buttons">
                {fields?.link?.value?.title && fields?.link?.value?.href && (
                  <CTA type={ctaTypes.brandPrimary} field={fields.link} target={targetLinkdsk(fields?.link, isDesktop)} />
                )}
                <DownloadCTA type={ctaTypes['primaryWhite']} field={fields?.downloadButtonLink} />
                <div className="slide__buttons--popup-link">
                  <Placeholder name={'jss-generic-popup'} rendering={rendering as ComponentRendering} />
                </div>
              </div>
            </FadeDown>
          </>
        )}
      </div>
    </div>
  );
};

export default HeroCarouselSlide;
